import { useGetIdentity, useGetList } from "ra-core";
import { useQuery } from "@tanstack/react-query";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { useOffers } from "../useOffers";
import { fillTemplate, withOptOut } from "./fillTemplate";

export type Template = {
  id: number;
  offer_id: number | null;
  channel: string;
  name: string;
  subject: string | null;
  body: string;
  pending_validation: boolean;
};

export type TemplateTarget = {
  template_id: number | null;
  offer_slug: string | null;
  company_name: string | null;
  first_name: string | null;
  giro: string | null;
  city: string | null;
};

/** Liga de agenda (Cal.com) de IA y ajustes: va en {{agenda}}, guion y sesión. */
export const useAgendaUrl = () =>
  useQuery({
    queryKey: ["crm_settings", "agenda_url"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await getSupabaseClient()
        .from("crm_settings")
        .select("agenda_url")
        .eq("id", 1)
        .maybeSingle();
      return (data?.agenda_url as string | null) ?? null;
    },
  }).data ?? null;

/**
 * Plantillas activas y cómo rellenarlas. options(target, canal) = las del
 * producto en ese canal (primero la de la tarea); fill() las rellena con las
 * variables y la opción de baja.
 */
export const useTemplates = () => {
  const { data: templates } = useGetList<Template>(
    "templates",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "id", order: "ASC" },
      filter: { active: true },
    },
    { staleTime: 5 * 60 * 1000 },
  );
  const { data: offers } = useOffers();
  const { identity } = useGetIdentity();
  const agenda = useAgendaUrl();

  const options = (target: TemplateTarget, channel: string) => {
    const offerId = offers?.find((o) => o.slug === target.offer_slug)?.id;
    return (templates ?? [])
      .filter(
        (t) =>
          t.channel === channel &&
          (t.id === target.template_id || t.offer_id === offerId),
      )
      .sort(
        (a, b) =>
          Number(b.id === target.template_id) -
          Number(a.id === target.template_id),
      );
  };
  const fill = (t: Template, target: TemplateTarget) => {
    const vars = {
      empresa: target.company_name,
      nombre: target.first_name,
      giro: target.giro?.toLowerCase(),
      municipio: target.city,
      remitente: identity?.fullName?.split(" ")[0],
      agenda,
    };
    return {
      subject: t.subject ? fillTemplate(t.subject, vars) : undefined,
      body: withOptOut(t.channel, fillTemplate(t.body, vars)),
      pending_validation: t.pending_validation,
    };
  };
  return { options, fill };
};

/**
 * Devuelve una función que elige la plantilla para (tarea, canal) — la de la
 * tarea si coincide el canal, si no la primera activa de la oferta — y la
 * rellena con las variables. null si no hay plantilla.
 */
export const useTemplateFor = () => {
  const { options, fill } = useTemplates();
  return (target: TemplateTarget, channel: string) => {
    const t = options(target, channel)[0];
    return t ? fill(t, target) : null;
  };
};
